/**
 * Quiz Analytics Service
 *
 * Tracks user responses to quiz questions for analytics
 * Supports both authenticated and anonymous users
 */

import { supabase } from '../lib/supabase/client';
import type { Database } from '../lib/supabase/types';

type QuizResponseInsert = Database['public']['Tables']['quiz_responses']['Insert'];
type QuizQuestionStats = Database['public']['Tables']['quiz_question_stats']['Row'];

// Session ID storage key
const SESSION_ID_KEY = 'quiz_session_id';

/**
 * Get or create a unique session ID for anonymous tracking
 */
export function getQuizSessionId(): string {
  let sessionId = sessionStorage.getItem(SESSION_ID_KEY);

  if (!sessionId) {
    // Generate a unique session ID
    sessionId = `anon_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
    sessionStorage.setItem(SESSION_ID_KEY, sessionId);
  }

  return sessionId;
}

/**
 * Record a quiz response
 */
export async function recordQuizResponse(params: {
  questionId: string;
  category: string;
  difficulty: 'easy' | 'medium' | 'hard';
  selectedAnswer: number;
  correctAnswer: number;
  isCorrect: boolean;
  timeSpentMs?: number;
}): Promise<{ success: boolean; error?: string }> {
  try {
    // Get current user (may be null for anonymous)
    const { data: { user } } = await supabase.auth.getUser();

    const sessionId = getQuizSessionId();

    const response: QuizResponseInsert = {
      user_id: user?.id || null,
      session_id: sessionId,
      question_id: params.questionId,
      category: params.category,
      difficulty: params.difficulty,
      selected_answer: params.selectedAnswer,
      correct_answer: params.correctAnswer,
      is_correct: params.isCorrect,
      time_spent_ms: params.timeSpentMs || null,
    };

    const { error } = await supabase
      .from('quiz_responses')
      .insert(response);

    if (error) {
      console.error('Error recording quiz response:', error);
      return { success: false, error: error.message };
    }

    return { success: true };
  } catch (error) {
    console.error('Error recording quiz response:', error);
    return { success: false, error: 'Failed to record response' };
  }
}

/**
 * Get questions with highest error rates (for content planning)
 */
export async function getQuestionsWithHighestErrorRates(
  limit: number = 20
): Promise<{ data: QuizQuestionStats[] | null; error?: string }> {
  try {
    const { data, error } = await supabase
      .from('quiz_question_stats')
      .select('*')
      .gte('total_attempts', 5) // Only questions with enough data
      .order('error_rate', { ascending: false })
      .order('total_attempts', { ascending: false })
      .limit(limit);

    if (error) {
      console.error('Error fetching question stats:', error);
      return { data: null, error: error.message };
    }

    return { data };
  } catch (error) {
    console.error('Error fetching question stats:', error);
    return { data: null, error: 'Failed to fetch stats' };
  }
}

/**
 * Get category performance summary
 */
export async function getCategoryPerformance(): Promise<{
  data: Array<{
    category: string;
    total_attempts: number;
    correct_attempts: number;
    avg_error_rate: number;
    questions_answered: number;
  }> | null;
  error?: string;
}> {
  try {
    const { data, error } = await supabase
      .from('quiz_question_stats')
      .select('category, total_attempts, correct_attempts');

    if (error) {
      console.error('Error fetching category performance:', error);
      return { data: null, error: error.message };
    }

    // Aggregate by category
    const categoryMap = new Map<string, {
      total_attempts: number;
      correct_attempts: number;
      questions: Set<string>;
    }>();

    data?.forEach(row => {
      const existing = categoryMap.get(row.category) || {
        total_attempts: 0,
        correct_attempts: 0,
        questions: new Set<string>(),
      };

      categoryMap.set(row.category, {
        total_attempts: existing.total_attempts + row.total_attempts,
        correct_attempts: existing.correct_attempts + row.correct_attempts,
        questions: existing.questions,
      });
    });

    const result = Array.from(categoryMap.entries()).map(([category, stats]) => ({
      category,
      total_attempts: stats.total_attempts,
      correct_attempts: stats.correct_attempts,
      avg_error_rate: stats.total_attempts > 0
        ? Math.round((1 - stats.correct_attempts / stats.total_attempts) * 10000) / 100
        : 0,
      questions_answered: stats.questions.size,
    }));

    return { data: result.sort((a, b) => b.avg_error_rate - a.avg_error_rate) };
  } catch (error) {
    console.error('Error fetching category performance:', error);
    return { data: null, error: 'Failed to fetch performance' };
  }
}

/**
 * Get user's personal quiz history (authenticated users only)
 */
export async function getUserQuizHistory(limit: number = 50): Promise<{
  data: Array<{
    question_id: string;
    category: string;
    is_correct: boolean;
    created_at: string;
  }> | null;
  error?: string;
}> {
  try {
    const { data: { user } } = await supabase.auth.getUser();

    if (!user) {
      return { data: null, error: 'Not authenticated' };
    }

    const { data, error } = await supabase
      .from('quiz_responses')
      .select('question_id, category, is_correct, created_at')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) {
      console.error('Error fetching user quiz history:', error);
      return { data: null, error: error.message };
    }

    return { data };
  } catch (error) {
    console.error('Error fetching user quiz history:', error);
    return { data: null, error: 'Failed to fetch history' };
  }
}

/**
 * Get overall quiz statistics
 */
export async function getOverallQuizStats(): Promise<{
  data: {
    totalResponses: number;
    totalCorrect: number;
    overallAccuracy: number;
    uniqueQuestions: number;
    uniqueUsers: number;
  } | null;
  error?: string;
}> {
  try {
    // Get total responses count
    const { count: totalResponses, error: countError } = await supabase
      .from('quiz_responses')
      .select('*', { count: 'exact', head: true });

    if (countError) {
      return { data: null, error: countError.message };
    }

    // Get correct responses count
    const { count: totalCorrect, error: correctError } = await supabase
      .from('quiz_responses')
      .select('*', { count: 'exact', head: true })
      .eq('is_correct', true);

    if (correctError) {
      return { data: null, error: correctError.message };
    }

    // Get unique questions count
    const { count: uniqueQuestions, error: questionsError } = await supabase
      .from('quiz_question_stats')
      .select('*', { count: 'exact', head: true });

    if (questionsError) {
      return { data: null, error: questionsError.message };
    }

    return {
      data: {
        totalResponses: totalResponses || 0,
        totalCorrect: totalCorrect || 0,
        overallAccuracy: totalResponses && totalResponses > 0
          ? Math.round((totalCorrect || 0) / totalResponses * 100)
          : 0,
        uniqueQuestions: uniqueQuestions || 0,
        uniqueUsers: 0, // Would need distinct count query
      },
    };
  } catch (error) {
    console.error('Error fetching overall stats:', error);
    return { data: null, error: 'Failed to fetch stats' };
  }
}
